import { useState } from 'react';
import { api, ComponentView, Environment, ProjectDeployment } from '../api';
import { RollbackDialog } from './RollbackDialog';
import { useResource } from '../hooks';
import { dbChangeLabel, humanize } from '../labels';
import { ErrorNotice, LoadingRows, StatusBadge } from './ui';
import { Drawer, RowActions, Table, clickableRow } from './primitives';
import { QuietEmpty } from './project/ui';

interface ComponentHistoryPanelProps {
  projectId: string;
  environment: Environment;
  component: ComponentView;
  onClose: () => void;
  onOpenDeployment: (deploymentId: string) => void;
  onRedeploy: () => void;
}


/**
 * ComponentHistoryPanel is one component's own release history in one
 * environment: every deployment that deployed it, which one is live, and
 * the rollback of this component alone.
 */
export function ComponentHistoryPanel({ projectId, environment, component, onClose, onOpenDeployment, onRedeploy }: ComponentHistoryPanelProps) {
  const app = component.application;
  const history = useResource<{ deployments: ProjectDeployment[] }>(
    () => api.listComponentDeployments(projectId, app.id, environment.id),
    [projectId, app.id, environment.id],
  );
  const [rollbackOf, setRollbackOf] = useState<ProjectDeployment | null>(null);
  const activeReleaseId = component.active_release?.id;
  const deployments = history.data?.deployments ?? [];

  return (
    <>
      <Drawer
        open
        size="wide"
        title={`${app.name} in ${environment.name}`}
        description={
          component.active_release ? (
            <>
              Live · <code>{component.active_release.commit_sha.slice(0, 7)}</code> ({component.active_release.release_tag})
            </>
          ) : (
            'Live · none'
          )
        }
        onClose={onClose}
        testId="component-history"
        footer={
          <>
            <button type="button" className="button button--secondary" onClick={onClose}>
              Close
            </button>
            <button
              type="button"
              className="button button--primary"
              onClick={onRedeploy}
              disabled={Boolean(app.disabled)}
              title={app.disabled ? `${app.name} is disabled; enable it in its settings to deploy it.` : undefined}
            >
              Deploy {app.name}
            </button>
          </>
        }
      >
        <ErrorNotice error={history.error} />
        {history.loading && !history.data ? <LoadingRows label="Loading history" rows={3} /> : null}
        {history.data && deployments.length === 0 ? (
          <QuietEmpty>No deployments</QuietEmpty>
        ) : null}
        {deployments.length > 0 ? (
          <Table label={`Deployments of ${app.name}`}>
            <thead>
              <tr>
                <th scope="col">Commit</th>
                <th scope="col">Status</th>
                {app.type === 'magento_backend' ? <th scope="col">Database</th> : null}
                <th scope="col">By</th>
                <th scope="col">When</th>
                <th scope="col">
                  <span className="visually-hidden">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {deployments.map((d) => {
                const isFrontendRow = app.type === 'frontend';
                const sha = isFrontendRow && d.frontend_commit_sha ? d.frontend_commit_sha : d.source_commit_sha;
                const releaseId = isFrontendRow && d.frontend_release_id ? d.frontend_release_id : d.release_id;
                const live = activeReleaseId && releaseId === activeReleaseId;
                const canRollBack = live && (d.status === 'completed' || d.status === 'completed_with_failures');
                const notes = [d.group_id ? 'coordinated' : '', d.scope === 'combined' ? 'with Magento' : ''].filter(Boolean).join(' · ');
                return (
                  <tr key={d.id} {...clickableRow(() => onOpenDeployment(d.id))}>
                    <th scope="row">
                      <button type="button" className="link-button" onClick={() => onOpenDeployment(d.id)} aria-label={`Open the ${sha.slice(0, 7)} deployment`}>
                        <code>{sha.slice(0, 7)}</code>
                      </button>
                      {live ? (
                        <>
                          {' '}
                          <StatusBadge kind="deployment" value="completed" label="Live now" />
                        </>
                      ) : null}
                      <div className="table__sub">
                        {(isFrontendRow ? d.frontend_commit_message : d.source_commit_message) || 'Deployment'}
                        {notes ? ` · ${notes}` : ''}
                      </div>
                    </th>
                    <td>
                      <StatusBadge kind="deployment" value={d.status} />
                      {d.failure_reason && d.status !== 'completed' ? <div className="table__sub">{d.failure_reason}</div> : null}
                    </td>
                    {app.type === 'magento_backend' ? <td>{dbChangeLabel[d.db_change_classification] ?? humanize(d.db_change_classification)}</td> : null}
                    <td>{d.created_by}</td>
                    <td>{new Date(d.created_at).toLocaleString()}</td>
                    <td>
                      <RowActions>
                        {canRollBack ? (
                          <button type="button" className="button button--danger-quiet button--small" onClick={() => setRollbackOf(d)} data-testid="component-rollback">
                            Roll back
                          </button>
                        ) : null}
                      </RowActions>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        ) : null}
      </Drawer>
      {/* The one rollback confirmation (UX-06): from → to, database,
          impact and a reason — the same as the deployment detail's. */}
      {rollbackOf ? (
        <RollbackDialog
          deploymentId={rollbackOf.id}
          environmentName={environment.name}
          onClose={() => setRollbackOf(null)}
          onRolledBack={() => {
            setRollbackOf(null);
            history.reload();
          }}
        />
      ) : null}
    </>
  );
}
